/**
 * Created by Jerker Nord on 2021-01-27.
 */
import {NolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/NolimitAudioPlayer";
import {DjPsychoGameMode} from "../server/DjPsychoGameMode";
import {DjPsycho} from "../DjPsycho";
import {IFadeTrackConf, IPlayTrackConf} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {NolimitHowl} from "@nolimitcity/slot-audio/bin/NolimitHowl";
import {gsap, TweenLite} from "gsap";
import {DjPsychoWinTypes, IWinRatioConfig} from "../server/IDjPsychoData";

export class DjPsychoSound {
    public static AS_WIN: string = "asWin";
    public static AS_WIN_BIG: string = "asWinBig";

    public static MAIN_GAME_MUSIC: string = "background-music-main";
    public static FS_INTRO_MUSIC: string = "fsIntro";
    public static FS_MUSIC: string = "fs";
    public static SFS_INTRO_MUSIC: string = "sfsIntro";
    public static SFS_MUSIC: string = "sfs";
    public static OUTRO_MUSIC: string = "fsOutro";

    public static REELS_SPINNING: string = "reelsSpinning";
    public static ROW_LANDING: string = "rowLanding";
    public static BET_LINE_HIGHLIGHT: string = "betlineHighlight";
    public static BET_LINES_REMOVE: string = "betlinesRemove";
    public static BET_LINES_ADD: string = "betlinesAdd";
    public static COUNT_UP: string = "countup";
    public static COUNT_UP_END_TINY: string = "countupEndsTiny";
    public static COUNT_UP_END_SMALL: string = "countupEndsSmall";
    public static COUNT_UP_END_MEDIUM: string = "countupEndsMedium";
    public static COUNT_UP_END_LARGE: string = "countupEndsLarge";
    public static COUNT_UP_END_FS: string = "countupEndsFS";
    public static BIG_WIN_NAME: string = "bigwin";
    public static BIG_WIN_ENDS: string = "bigwinEnds";
    public static PAD_SOUND_NAME: string = "pad";
    public static CAP_WIN_COUNT_UP: string = "capwinCountup";
    public static CAP_WIN_MUSIC: string = "capwinMusic";
    public static CAP_WIN_ENDS: string = "capwinEnds";
    public static FEATURE_X_KNOB: string = "featureXKnob";
    public static MULTIPLIER_UP_SOUNDS: string[] = ["multiplierUp1", "multiplierUp2", "multiplierUp3", "multiplierUp4", "multiplierUp5", "multiplierUp6"];
    public static FEATURE_BREAK: string = "featureBreak";
    public static FEATURE_BITE: string = "featureBite";
    public static FEATURE_DROP: string = "featureDrop";
    public static FEATURE_X_BPM: string = "featureXBPM";
    public static FEATURE_TURN_TO_WILD: string = "featureTurnToWild";
    public static X_BET_ON: string = "xBetOn";
    public static X_BET_OFF: string = "xBetOff";
    public static FS_BUY_METER_MUSIC: string = "fsBuyMeterMusic";
    public static FS_BUY_METER_MUSIC_ENDS: string = "fsBuyMeterMusicEnds";
    public static FS_BUY_METER_FILL: string = "fsBuyMeterFill";
    public static FS_BUY_METER_EMPTY: string = "fsBuyMeterEmpty";
    public static FS_BUY_METER_HIGHLIGHT: string = "fsBuyMeterHighlight";
    public static FS_INTRO_START: string = "fsIntroStart";
    public static FS_START: string = "fsStart";
    public static SFS_INTRO_START: string = "sfsIntroStart";
    public static SFS_START: string = "sfsStart";
    public static FS_METER_FILL: string = "fsMeterFill";
    public static FS_METER_ADD_MULTI: string = "fsMeterAddMulti";
    public static FS_METER_MULTI_UP: string = "fsMeterMultiUp";
    public static FS_METER_FULL3: string = "fsMeterFull3";
    public static FS_METER_FULL5: string = "fsMeterFull5";
    public static FS_METER_FS_WIN: string = "fsMeterFsWin";
    public static FS_PLUS_SPINS: string = "fsPlusSpins";
    public static NO_WIN_SCREENS: string[] = ["noWinFuck", "noWinNoMoreBass", "noWinWrongVibes"];
    public static BLINK: string = "blink";
    public static BLINK_ENDS: string = "blinkEnds";
    public static SCRATCH: string = "scratch";
    public static COUNT_UP_ENDS_FS_BAD: string = "countupEndsFSBad";

    private _currentMusic: string;
    private _countUpSound: NolimitHowl | undefined;
    private _reelsSpinTween: TweenLite;

    private player: NolimitAudioPlayer;

    constructor() {
        this.player = <NolimitAudioPlayer>DjPsycho.sound;
        this.player.showLogs(true);
    }

    private static createConfig(name: string, volume?: number, repeat?: number): IPlayTrackConf {
        return {
            name: name,
            volume: volume,
            repeat: repeat
        };
    }

    public playSound(name: string, volume?: number, repeat?: number): NolimitHowl | undefined {
        return this.player.playTrack(DjPsychoSound.createConfig(name, volume, repeat));
    }

    public stopSound(name: string): void {
        this.player.stopTrack(name);
    }

    // #163
    public killReelsSpinSound(): void {
        if (this._reelsSpinTween) {
            this._reelsSpinTween.pause();
            this._reelsSpinTween.kill();
        }
    }

    public fadeOutEffect(fadeOutConf: IFadeTrackConf): void {
        const fade: NolimitHowl | undefined = this.player.fadeTrack(fadeOutConf);

        if (fade) {
            this._reelsSpinTween = gsap.to(this, {
                duration: fadeOutConf.duration / 1000, onComplete: () => {
                    this.player.stopTrack(fadeOutConf.name);
                }
            });
        } else {
            this.player.stopTrack(fadeOutConf.name);
        }
    }

    public switchMusic(mode: string): void {
        const nextMusic: string = this.getNextMusicName(mode);

        if (nextMusic === this._currentMusic) {
            return;
        }

        if (this._currentMusic) {
            this.player.stopTrack(this._currentMusic);
        }

        this.player.playTrack(DjPsychoSound.createConfig(nextMusic, 1, undefined));
        this._currentMusic = nextMusic;
    }

    public fadeMusic(fadeConfig: IFadeTrackConf): void {
        fadeConfig.name = fadeConfig.name || this._currentMusic;
        this.player.fadeTrack(fadeConfig);
    }

    public fadeMusicOnOutro(): void {
        const nextMusic: string = DjPsychoSound.OUTRO_MUSIC;

        if (nextMusic === this._currentMusic) {
            return;
        }

        if (this._currentMusic) {
            this.player.stopTrack(this._currentMusic);
        }

        this.player.fadeTrack({
            name: nextMusic,
            duration: 1000,
            to: 1
        });
        this._currentMusic = nextMusic;
    }

    public startCountUp(): void {
        this._countUpSound = this.playSound(DjPsychoSound.COUNT_UP);
        if (this._countUpSound) {
            this._countUpSound.setLoop(true);
        }
    }

    public stopCountUp(): void {
        if (this._countUpSound && this._countUpSound.isPlaying) {
            this._countUpSound.stop();
        }
        let winRatioConfig: IWinRatioConfig = DjPsycho.model.winRatioConfig;
        let sound: string = DjPsychoSound.COUNT_UP_END_LARGE;

        if (winRatioConfig) {
            if (winRatioConfig.type === DjPsychoWinTypes.TINY) {
                sound = DjPsychoSound.COUNT_UP_END_TINY;
            } else if (winRatioConfig.type === DjPsychoWinTypes.SMALL) {
                sound = DjPsychoSound.COUNT_UP_END_SMALL;
            } else if (winRatioConfig.type === DjPsychoWinTypes.MEDIUM) {
                sound = DjPsychoSound.COUNT_UP_END_MEDIUM;
            }
        }
        this.playSound(sound);
    }

    public playFsEndCountUp(): void {
        if (this._countUpSound && this._countUpSound.isPlaying) {
            this._countUpSound.stop();
        }
        this.playSound(DjPsychoSound.COUNT_UP_END_FS);
    }

    // #50
    private getNextMusicName(mode: string): string {
        let nextMusic: string;

        switch (mode) {
            case DjPsychoSound.FS_INTRO_MUSIC:
                nextMusic = DjPsychoSound.FS_INTRO_MUSIC;
                break;
            case DjPsychoGameMode.FREESPIN:
            case DjPsychoGameMode.FREESPIN_AVALANCHE:
                nextMusic = DjPsychoSound.FS_MUSIC;
                break;
            case DjPsychoSound.SFS_INTRO_MUSIC:
                nextMusic = DjPsychoSound.SFS_INTRO_MUSIC;
                break;
            case DjPsychoGameMode.FREESPIN_SUPER:
            case DjPsychoGameMode.FREESPIN_SUPER_AVALANCHE:
                nextMusic = DjPsychoSound.SFS_MUSIC;
                break;
            case DjPsychoSound.OUTRO_MUSIC:
                nextMusic = DjPsychoSound.OUTRO_MUSIC;
                break;
            default:
                nextMusic = DjPsychoSound.MAIN_GAME_MUSIC;
                break;
        }

        return nextMusic;
    }
}